<template>
  <div class="bg-white min-h-screen">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-20">
      <p class="text-gray-500">Loading podcast...</p>
    </div>
    <!-- Error State -->
    <div v-else-if="error" class="text-center py-20 max-w-2xl mx-auto">
      <h2 class="text-2xl font-bold text-red-700">Oops!</h2>
      <p class="mt-2 text-gray-600">{{ error }}</p>
    </div>
    <!-- Main Content -->
    <div v-else-if="client">
      <header 
        class="p-8 text-white"
        :style="{ backgroundColor: client.brandColour || '#1F2937' }"
      >
        <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-8">
          <!-- START: DEFINITIVE FIX FOR LOGO URL -->
          <img v-if="client.logoUrl" :src="client.logoUrl" alt="Podcast Cover Art" class="w-48 h-48 rounded-xl shadow-2xl flex-shrink-0">
          <!-- END: DEFINITIVE FIX FOR LOGO URL -->
          <div class="text-center sm:text-left">
            <p class="text-sm font-semibold uppercase tracking-wider opacity-80">Podcast</p>
            <h1 class="mt-2 text-4xl sm:text-5xl font-bold">{{ client.podcastName }}</h1>
            <p class="mt-4 text-lg opacity-90">{{ client.podcastSlogan }}</p>
          </div>
        </div>
      </header>

      <main class="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div class="flex flex-col sm:flex-row justify-between items-center border-b pb-4 mb-8">
          <h2 class="text-3xl font-bold text-gray-800">Episodes</h2>
          <div class="mt-4 sm:mt-0">
            <input 
              v-model="searchQuery"
              type="text" 
              placeholder="Search episodes..." 
              class="block w-full sm:w-64 border-gray-300 rounded-full shadow-sm py-2 px-4 focus:ring-brand-purple-dark focus:border-brand-purple-dark"
            >
          </div>
        </div>

        <div v-if="filteredEpisodes.length === 0" class="text-center text-gray-500 py-12">
          <p>{{ searchQuery ? 'No episodes match your search.' : 'No published episodes yet. Check back soon!' }}</p>
        </div>
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <router-link v-for="episode in filteredEpisodes" :key="episode.id" :to="`/shows/${client.slug}/${episode.id}`" class="group bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300">
            <div class="aspect-w-1 aspect-h-1">
              <!-- START: DEFINITIVE FIX FOR EPISODE ART -->
              <img :src="episode.episodeCoverArtUrl || client.logoUrl" alt="Episode Cover Art" class="w-full h-full object-cover">
              <!-- END: DEFINITIVE FIX FOR EPISODE ART -->
            </div>
            <div class="p-4">
              <h3 class="text-base font-bold text-gray-800 group-hover:text-brand-purple-dark transition-colors duration-200">{{ episode.title }}</h3>
              <p class="text-xs text-gray-500 mt-1">{{ formatDate(episode.createdAt) }}</p>
            </div>
          </router-link>
        </div>
      </main>
    </div>
    <footer class="text-center py-6">
      <a href="https://castive.app" target="_blank" rel="noopener noreferrer" class="inline-flex items-center space-x-2">
        <img src="@/assets/logo.png" alt="Castive Logo" class="h-5 w-auto opacity-75">
        <span class="text-sm text-gray-600 font-medium">Powered by Castive</span>
      </a>
    </footer>
  </div>
</template>

<script>
import { db } from '@/firebase';
// --- START: DEFINITIVE CACHE FIX ---
import { getDocsFromServer, collection, query, where, orderBy } from 'firebase/firestore';
// --- END: DEFINITIVE CACHE FIX ---

export default {
  name: 'PublicPodcastPage',
  data() {
    return {
      client: null,
      clientId: null,
      episodes: [],
      loading: true,
      error: null,
      searchQuery: '',
    };
  },
  computed: {
    filteredEpisodes() {
      if (!this.searchQuery) {
        return this.episodes;
      }
      const lowerCaseQuery = this.searchQuery.toLowerCase();
      return this.episodes.filter(episode => {
        const titleMatch = episode.title.toLowerCase().includes(lowerCaseQuery);
        const notesMatch = episode.showNotes.toLowerCase().includes(lowerCaseQuery);
        return titleMatch || notesMatch;
      });
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp?.toDate) return '';
      const date = timestamp.toDate();
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    },
  },
  async created() {
    try {
      const slug = this.$route.params.slug;
      if (!slug) throw new Error('No podcast specified.');

      const clientsQuery = query(collection(db, 'clients'), where('slug', '==', slug));
      // --- DEFINITIVE CACHE FIX: Use getDocsFromServer ---
      const clientSnapshot = await getDocsFromServer(clientsQuery);
      if (clientSnapshot.empty) { throw new Error('This podcast could not be found.'); }
      
      const clientDoc = clientSnapshot.docs[0];
      this.client = clientDoc.data();
      this.clientId = clientDoc.id;

      const episodesQuery = query(
        collection(db, 'episodes'),
        where('clientId', '==', this.clientId),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc')
      );
      // --- DEFINITIVE CACHE FIX: Use getDocsFromServer ---
      const episodesSnap = await getDocsFromServer(episodesQuery);
      this.episodes = episodesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.aspect-w-1 {
  aspect-ratio: 1 / 1;
}
</style>